import TagCloud, { type TagCloudOptions } from '@frank-mayer/react-tag-cloud';
import axios from 'axios';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';

import ResumeSection from '../../components/ResumeSection';
import SkillGauge from '../../components/SkillGauge';

import styles from './Resume.module.scss';

interface SkillTag {
  value: string;
  score: number;
  detail: string;
}

interface ResumeSectionData {
  eventKey: string;
  title: string;
  type: 'html' | 'skills';
  content?: React.ReactNode;
}

const Resume = () => {
  const [resumeData, setResumeData] = useState<ResumeSectionData[]>([]);
  const [skillMatrix, setSkillMatrix] = useState<SkillTag[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<SkillTag | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [skillViewMode, setSkillViewMode] = useState<'cloud' | 'list'>('cloud');

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [resumeResp, skillsResp] = await Promise.all([
          axios.get<ResumeSectionData[]>('/json/resumeData.json'),
          axios.get<SkillTag[]>('/json/skillMatrix.json'),
        ]);

        // Normalize resume response to an array in case the JSON is an object map
        let data: ResumeSectionData[] = [];
        if (Array.isArray(resumeResp.data)) data = resumeResp.data;
        else if (resumeResp.data && typeof resumeResp.data === 'object')
          data = Object.values(resumeResp.data) as ResumeSectionData[];

        // Debugging: log fetched length to help diagnose partial rendering
        console.debug('resume fetch', { length: data.length });

        if (!cancelled) {
          setResumeData(data);
          setSkillMatrix(skillsResp.data ?? []);
        }
      } catch (err) {
        console.error('Failed to load resume data', err);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const name = 'Hal Barnett';
  const [activeKey, setActiveKey] = useState<string | string[] | null>(['0']);

  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const openSkillDialog = (tag: SkillTag) => {
    setSelectedSkill(tag);
    dialogRef.current?.showModal();
  }; // NEW

  const closeSkillDialog = () => {
    dialogRef.current?.close();
    setSelectedSkill(null);
  }; // NEW

  const renderSectionContent = (section: ResumeSectionData) => {
    if (section.type === 'skills') {
      return (
        <div className={styles['skills-section']}>
          <div className={styles['skills-view-toggle']}>
            <div
              className="btn-group"
              role="group"
              aria-label="Skill view toggle"
            >
              <button
                type="button"
                className={clsx(
                  'btn btn-sm',
                  skillViewMode === 'cloud' && styles.active,
                )}
                onClick={() => setSkillViewMode('cloud')}
                aria-pressed={skillViewMode === 'cloud'}
              >
                Cloud View
              </button>
              <button
                type="button"
                className={clsx(
                  'btn btn-sm',
                  skillViewMode === 'list' && styles.active,
                )}
                onClick={() => setSkillViewMode('list')}
                aria-pressed={skillViewMode === 'list'}
              >
                List View
              </button>
            </div>
          </div>

          {skillViewMode === 'cloud' ? (
            <div>
              <h6 className="text-center small">
                Click an item for more detail
              </h6>
              <TagCloud
                options={(w: Window & typeof globalThis): TagCloudOptions => ({
                  radius: Math.min(350, w.innerWidth, w.innerHeight) / 2,
                  maxSpeed: 'fast',
                  initSpeed: 'slow',
                  useHTML: true,
                })}
                className="oba-tag-cloud"
                onClick={(tag: string) => {
                  const skillData = skillMatrix.find((s) => s.value === tag);
                  if (skillData) openSkillDialog(skillData);
                  else if (tag === name)
                    openSkillDialog({
                      value: 'Hello there!',
                      score: 0,
                      detail: 'These are just a sampling of my skills.',
                    });
                }}
              >
                {[...skillMatrix.map((tag) => tag.value), name]}
              </TagCloud>
            </div>
          ) : (
            <div className={styles['skills-list']}>
              {skillMatrix.map((skill) => (
                <div key={skill.value} className={styles['skill-item']}>
                  <div className={styles['skill-header']}>
                    <h5>{skill.value}</h5>
                    <SkillGauge
                      level={(skill.score / 100) * 5}
                      color="var(--brand-color-2)"
                    />
                  </div>
                  <p className={styles['skill-detail']}>{skill.detail}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    // When content comes from JSON we expect HTML strings for 'html' type
    if (typeof section.content === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: section.content }} />;
    }
    return section.content;
  };

  const checkKeys = (key: string) => {
    const isActiveKeyArray = Array.isArray(activeKey);
    if (activeKey === null || (Array.isArray(activeKey) && !activeKey.length))
      return [key];
    if (isActiveKeyArray && !activeKey.includes(key)) {
      return [...activeKey, key];
    }
    return isActiveKeyArray ? activeKey : [activeKey];
  };

  const handleKeyChange = (key: string) => {
    const newKeys = checkKeys(key);
    if (newKeys.length) {
      const headerEl = headerRefs.current[Number(key)];
      if (headerEl) {
        setTimeout(() => {
          headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(() => {
            headerEl
              .querySelector<HTMLButtonElement>('.accordion-button')
              ?.focus();
          }, 700);
        }, 350);
      }
    }
    setActiveKey(newKeys);
  };

  return (
    <div>
      <h2 className="mb-4">Resume for {name}</h2>

      {/* NEW: skill details dialog */}
      <dialog
        ref={dialogRef}
        className={styles['skill-dialog']}
        onClose={closeSkillDialog}
      >
        {selectedSkill && (
          <div>
            <div className={styles['skill-header']}>
              <h3>{selectedSkill.value}</h3>
              {!!selectedSkill.score && (
                <SkillGauge
                  level={(selectedSkill.score / 100) * 5}
                  color="var(--brand-color-2)"
                />
              )}
            </div>
            {selectedSkill.detail && <p>{selectedSkill.detail}</p>}
          </div>
        )}
        <button
          type="button"
          className={styles['skill-dialog-close']}
          onClick={closeSkillDialog}
        >
          X
        </button>
      </dialog>

      <Accordion
        alwaysOpen
        activeKey={activeKey}
        onSelect={(key) => setActiveKey(key ?? null)}
        className={styles['accordion']}
      >
        {resumeData.map((section, index) => {
          const prevSection = resumeData[index - 1];
          const nextSection = resumeData[index + 1];

          return (
            <ResumeSection
              key={section.eventKey}
              eventKey={section.eventKey}
              title={section.title}
              onNavigate={handleKeyChange}
              prevButtonText={prevSection?.title}
              prevEventKey={prevSection?.eventKey}
              nextButtonText={nextSection?.title}
              nextEventKey={nextSection?.eventKey}
              headerRef={(el) => (headerRefs.current[index] = el)}
            >
              {renderSectionContent(section)}
            </ResumeSection>
          );
        })}
      </Accordion>
    </div>
  );
};

export default Resume;
